import {
  Button,
  Container,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowRight,
  IconCheck,
  IconClockHour4,
  IconFileText,
  IconFlask2,
  IconLeaf,
  IconMessageCircle,
  IconMicroscope,
  IconNotebook,
  IconRosetteDiscountCheck,
  IconShieldCheck,
  IconStarFilled,
  IconUserCheck,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { buildWhatsAppUrl } from "../../../Services/contact.service";
import {
  getAllReviews,
  getReviewsByAssignmentSlug,
  getReviewSummary,
} from "../../../Services/review.service";
import { getAssignmentPageBySlug } from "../../../data/assignmentPages";
import Seo from "../../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../../seo/utils";
import { getAssignmentPagePath } from "../../../utils/routes";
import AssignmentFAQ from "../AssignmentFAQ";
import Breadcrumbs from "../Breadcrumbs";
import GetHelpForm from "../../Shared/GetHelpForm";
import ReviewsPreviewSection from "../../Reviews/ReviewsPreviewSection";
import "../BotanyAssignmentPage2.css";

const pageSlug = "botany-assignment-help";

const trustSignals = [
  {
    label: "Assignment Help",
    score: "4.9",
    icon: IconShieldCheck,
  },
  {
    label: "Student Rated on Subject Buddy",
    score: "4.8",
    icon: IconUserCheck,
  },
  {
    label: "Verified Reviews from students directly",
    score: "4.9",
    icon: IconRosetteDiscountCheck,
  },
];

const scholarCards = [
  {
    name: "Elena Brooks",
    degree: "Ph.D. in Botany",
    initials: "EB",
    description:
      "Focuses on plant systematics, taxonomy, and botanical naming. She helps students organise classification-heavy assignments into clearer, more defensible arguments.",
    completedOrders: 118,
    studentReviews: 46,
    stars: 5,
    accent: "botany-v2-avatar-one",
  },
  {
    name: "Noah Bennett",
    degree: "MSc in Plant Biology",
    initials: "NB",
    description:
      "Works closely on plant physiology, growth, and molecular biology tasks. Best for reports that need stronger scientific explanation and cleaner results discussion.",
    completedOrders: 104,
    studentReviews: 39,
    stars: 4,
    accent: "botany-v2-avatar-two",
  },
  {
    name: "Maya Collins",
    degree: "Ph.D. in Botany",
    initials: "MC",
    description:
      "Supports students with lab reports, pathology topics, and data interpretation. Strong at turning rough notes into better organised scientific coursework.",
    completedOrders: 127,
    studentReviews: 58,
    stars: 5,
    accent: "botany-v2-avatar-three",
  },
  {
    name: "Lucas Reed",
    degree: "Ph.D. in Environmental Botany",
    initials: "LR",
    description:
      "Helps with ecology, sustainable agriculture, and environmental plant science assignments where structure and evidence need to work together more clearly.",
    completedOrders: 96,
    studentReviews: 31,
    stars: 4,
    accent: "botany-v2-avatar-four",
  },
];

const processSteps = [
  {
    title: "Submit Your Requirements",
    description:
      "Send us your botany assignment prompt, deadline details, and any notes your professor has already shared.",
    icon: IconNotebook,
  },
  {
    title: "Secure Payment",
    description:
      "Our experts connect with you for order pricing discussion, Pay advance 10-30% to get your work started and the rest after work completion.",
    icon: IconShieldCheck,
  },
  {
    title: "Receive Your Order",
    description:
      "Get your finished botany work before the due date with clear structure, better explanation, and personal tutor support.",
    icon: IconFileText,
  },
];

const featurePillars = [
  {
    title: "Original Writing",
    description:
      "We build every botany response around the exact task so the structure and explanation feel specific instead of generic.",
    icon: IconShieldCheck,
  },
  {
    title: "Day and Night Support",
    description:
      "You can share your brief, ask follow-up questions, and get direction whenever the assignment starts feeling unclear.",
    icon: IconMicroscope,
  },
  {
    title: "Accurate Science Data",
    description:
      "Plant science details, terminology, and evidence are checked carefully so the work reads more confidently and correctly.",
    icon: IconNotebook,
  },
  {
    title: "Correct Citations",
    description:
      "Useful for reports and essays that lose marks because references, in-text citations, or formatting feel inconsistent.",
    icon: IconClockHour4,
  },
  {
    title: "Fast Delivery",
    description:
      "When deadlines are close, we focus on the changes that improve clarity and quality fastest without wasting effort.",
    icon: IconFlask2,
  },
  {
    title: "Free Changes",
    description:
      "If a section still needs improvement after review, you can refine it further instead of feeling locked into one draft path.",
    icon: IconLeaf,
  },
];

const reviewCards = [
  {
    title: "Healthcare",
    meta: "Assignment guidance, U.S. student",
    quote:
      "The structure feedback was direct and easy to use. I knew exactly what to fix first instead of second-guessing every paragraph.",
    date: "07 Sep 2025",
  },
  {
    title: "Economics",
    meta: "Deadline support, U.S. student",
    quote:
      "The support was fast and practical. It helped me tighten the analysis and submit a cleaner piece without wasting time on low-value edits.",
    date: "05 Sep 2025",
  },
  {
    title: "Sociology",
    meta: "Draft review, U.S. student",
    quote:
      "Clear comments, better flow, and much less confusion. The final version felt more organised and more aligned with the brief.",
    date: "03 Sep 2025",
  },
];

const serviceTopics = [
  "Cytology",
  "Paleobotany",
  "Phytogeography",
  "Plant morphology",
  "Plant physiology",
  "Plant breeding",
  "Ecology and fieldwork",
  "Taxonomy and nomenclature",
];

const freeSupportItems = [
  "Structure feedback",
  "Referencing guidance",
  "Formatting checks",
  "Proofreading notes",
  "Title refinement",
  "In-text citation review",
];
const contentSections = [
  {
    title: "Why Specialised Botany Assignment Support Matters",
    paragraphs: [
      "Botany assignments require more than basic plant knowledge. Students need to explain scientific concepts clearly, use correct botanical terminology, connect theory with evidence, and present the answer in a proper academic structure.",
      "Specialised botany assignment support helps students improve the quality of their work by making complex topics easier to understand and write. Whether the task is about plant anatomy, physiology, taxonomy, ecology, or lab observations, expert guidance can help turn scattered ideas into a clear and well-organised submission.",
    ],
    bullets: [
      "Clear explanation of plant science concepts and terminology",
      "Better connection between theory, observations, and evidence",
      "More organised structure for essays, reports, and lab assignments",
    ],
  },
  {
    title: "Common Challenges in Botany Assignments",
    paragraphs: [
      "Many students struggle with botany assignments because the subject includes both theoretical and practical learning. A single task may require knowledge of plant cells, tissues, photosynthesis, classification systems, ecological relationships, and experimental data.",
      "Another common challenge is presenting the information in an academic way. Students may understand the topic but find it difficult to write strong introductions, explain results, create discussion sections, or use correct scientific names and references.",
    ],
    bullets: [
      "Difficulty explaining complex topics like photosynthesis, transpiration, and plant hormones",
      "Confusion with taxonomy, binomial nomenclature, and plant classification",
      "Weak lab report sections, especially methodology, results, and discussion",
    ],
  },
  {
    title: "How SubjectBuddy Helps With Botany Assignments",
    paragraphs: [
      "SubjectBuddy provides expert-led botany assignment help for students who need support with research, writing, editing, formatting, and final submission preparation. Our focus is to make your assignment clearer, stronger, and more aligned with your academic requirements.",
      "We help students understand the assignment brief, organise the content properly, improve scientific explanation, and present information in a clean academic format. This support is useful for homework tasks, essays, lab reports, research papers, and dissertation chapters.",
    ],
    bullets: [
      "Help with botany essays, homework, lab reports, and research papers",
      "Support for plant anatomy, physiology, taxonomy, ecology, and genetics topics",
      "Improved academic structure, clarity, formatting, and referencing",
    ],
  },
  {
    title: "Botany Topics Covered by Our Experts",
    paragraphs: [
      "Botany is a broad subject, and students often receive assignments from different areas of plant science. Our experts can help with both basic and advanced topics depending on your course level, deadline, and assignment instructions.",
      "From plant structure and growth to ecological adaptation and biotechnology, we support students with topic explanation, content planning, research direction, and assignment improvement.",
    ],
    bullets: [
      "Plant anatomy, plant physiology, and plant reproduction",
      "Photosynthesis, respiration, transpiration, and plant hormones",
      "Taxonomy, plant ecology, plant pathology, genetics, and biotechnology",
    ],
  },
  {
    title: "Botany Lab Report and Research Paper Help",
    paragraphs: [
      "Botany lab reports require clear presentation of aim, hypothesis, materials, methods, observations, results, discussion, and conclusion. Students often lose marks when the report is not structured properly or when results are presented without enough explanation.",
      "Our botany lab report help focuses on improving the academic flow of your report. We help you describe methods clearly, organise observations, explain findings, and connect your results back to the experiment objective or research question.",
    ],
    bullets: [
      "Clear IMRaD-style lab report structure",
      "Better explanation of observations, tables, figures, and results",
      "Stronger discussion and conclusion sections linked to the assignment aim",
    ],
  },
  {
    title: "Plant Anatomy and Physiology Assignment Help",
    paragraphs: [
      "Plant anatomy and physiology are two of the most common areas in botany assignments. Students may be asked to explain tissues, cells, vascular systems, growth processes, photosynthesis, respiration, or plant responses to environmental conditions.",
      "Our experts help simplify these technical concepts so they can be explained in a clear and academically suitable way. This makes the assignment easier to read and helps the final submission look more focused and confident.",
    ],
    bullets: [
      "Support with xylem, phloem, meristems, stomata, roots, stems, and leaves",
      "Explanation of photosynthesis, transpiration, respiration, and water movement",
      "Clear academic writing for technical plant biology concepts",
    ],
  },
  {
    title: "Taxonomy and Plant Classification Support",
    paragraphs: [
      "Taxonomy assignments can be confusing because they require accurate classification, scientific naming, and comparison between plant groups. Students may need to explain plant families, evolutionary relationships, phylogeny, or binomial nomenclature.",
      "SubjectBuddy helps students write taxonomy-based assignments with better clarity and accuracy. We support you in organising classification details, explaining relationships between plant groups, and using correct botanical terminology.",
    ],
    bullets: [
      "Help with binomial nomenclature and scientific naming",
      "Support for angiosperms, gymnosperms, bryophytes, and pteridophytes",
      "Clearer explanation of phylogeny, classification, and plant evolution",
    ],
  },
  {
    title: "Benefits of Choosing SubjectBuddy for Botany Assignment Help",
    paragraphs: [
      "Choosing the right academic support can make your botany assignment easier to complete and better in quality. SubjectBuddy focuses on practical, student-friendly support that helps you understand the topic and improve your final work.",
      "Our aim is not just to fill pages, but to help you create a more meaningful assignment with clearer explanations, better structure, stronger academic tone, and timely submission support.",
    ],
    bullets: [
      "Faster assignment completion with expert guidance",
      "Better quality work with clear scientific explanation",
      "On-time submission support for essays, reports, and research papers",
    ],
  },
  {
    title: "What This Botany Assignment Support Helps With",
    paragraphs: [
      "Our botany assignment help is designed for students who want to improve their academic work before submission. Whether you are stuck at the planning stage or need help improving a draft, SubjectBuddy can support you with the right guidance.",
      "We help you move from confusion to clarity by improving the content, structure, explanation, and presentation of your botany assignment.",
    ],
    bullets: [
      "Faster completion of botany assignments and homework",
      "Quality work submission with better structure and explanation",
      "Better grade potential through clearer academic writing and stronger analysis",
    ],
  },
  {
    title: "Get Online Botany Assignment Help Today",
    paragraphs: [
      "If you are struggling with a botany assignment, lab report, research paper, or dissertation chapter, SubjectBuddy can help you complete it with more confidence. Our experts provide support based on your topic, deadline, marking criteria, and university guidelines.",
      "Share your assignment brief, word count, deadline, and any instructions you have. We will help you improve the quality, clarity, and structure of your botany assignment so it is ready for submission.",
    ],
    bullets: [
      "Share your topic, deadline, rubric, and assignment instructions",
      "Get expert support for research, writing, editing, and formatting",
      "Submit a clearer, better-structured botany assignment on time",
    ],
  },
];

const sampleSection = {
  title: "Sample Academic Direction for a Botany Assignment",
  heading:
    "How vascular and reproductive efficiency support angiosperm success",
  copy: "A strong botany assignment sample should not simply list the features of flowering plants. It should explain how specific biological adaptations helped angiosperms become one of the most successful plant groups on Earth. For example, vessel elements in the xylem improved water transport efficiency, allowing faster movement of water and minerals through the plant body. This supported stronger growth, higher photosynthetic activity, and better survival in different environments. Similarly, double fertilization improved reproductive efficiency by ensuring that nutritive endosperm tissue develops only after successful fertilization, reducing unnecessary energy use. Flowers also increased the chances of pollination, while fruits helped protect seeds and support wider seed dispersal. A well-written answer should connect all these features together and show how transport efficiency, reproductive strategy, pollination, seed protection, and environmental adaptation created a strong evolutionary advantage for angiosperms.",
};

const botanyFaqs = [
  {
    question: "Can I get help with a botany report, essay, or lab assignment?",
    answer:
      "Yes. Support can focus on report structure, scientific explanation, data interpretation, discussion quality, and final draft improvement depending on the task.",
  },
  {
    question:
      "Do you help with taxonomy, physiology, and plant science topics?",
    answer:
      "Yes. Common requests include taxonomy, morphology, physiology, ecology, plant breeding, nomenclature, and coursework that needs better structure or clearer analysis.",
  },
  {
    question: "Can you support urgent botany assignment help?",
    answer:
      "Yes, depending on the deadline and the current state of the draft. Sharing the exact brief and due date is the fastest way to get useful direction.",
  },
  {
    question: "What should I send before requesting botany assignment help?",
    answer:
      "Share your assignment question, marking rubric, deadline, word count, referencing style, and any notes, data, or draft sections you already have.",
  },
  {
    question: "Can you help improve scientific clarity and referencing?",
    answer:
      "Yes. That includes terminology accuracy, paragraph flow, figure or table explanation, and checking how references are used across the assignment.",
  },
];

function BotanyAssignmentPage() {
  const theme = useMantineTheme();
  const desktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { hash } = useLocation();
  const page = getAssignmentPageBySlug(pageSlug);

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(hash.replace(/^#/, ""));

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [hash]);

  if (!page) {
    return null;
  }

  const whatsappUrl = buildWhatsAppUrl(
    "Hi, I need help with my Botany assignment.",
  );
  const reviewSummary = getReviewSummary();
  const relatedReviews = getReviewsByAssignmentSlug(page.slug, 3);
  const usReviews = getAllReviews()
    .filter((review) => review.country === "United States")
    .slice(0, 3);
  const orderPagePath = `/order-form?from=${page.slug}`;

  const seoTitle = "Botany Assignment Help | SubjectBuddy";
  const seoDescription =
    "Get botany assignment help for lab reports, taxonomy, plant physiology, ecology, and plant science coursework with structured academic guidance from SubjectBuddy.";

  return (
    <main className="botany-v2-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={getAssignmentPagePath(page.slug)}
        schema={[
          createOrganizationSchema(),
          createFaqSchema(botanyFaqs),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: page.title, path: getAssignmentPagePath(page.slug) },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.title,
            provider: {
              "@type": "Organization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            serviceType: "Botany assignment help",
            areaServed: [
              "United States",
              "United Kingdom",
              "Canada",
              "Australia",
            ],
            description: seoDescription,
          },
        ]}
      />

      <Container size="xl" className="botany-v2-shell">
        <section className="botany-v2-hero">
          <Breadcrumbs
            items={[{ label: "Home", href: "/" }, { label: page.title }]}
          />

          <div className="botany-v2-hero-grid">
            <div className="botany-v2-hero-copy">
              <Title order={1} className="botany-v2-title">
                Need Botany Assignment Help ? Expert Guidance for Students
              </Title>
              <Text className="botany-v2-subtitle">
                Finding the right support for complex plant science can be
                difficult. If you are looking for reliable botany assignment
                help, SubjectBuddy provides clear, accurate, and practical
                guidance that helps students improve structure, scientific
                explanation, and final submission quality.
              </Text>

              <div className="botany-v2-trust">
                Trusted by <strong>{reviewSummary.totalReviews}+</strong>{" "}
                students seeking clearer assignment support
              </div>

              <div className="botany-v2-trust-row">
                {trustSignals.map((item) => (
                  <div key={item.label} className="botany-v2-trust-card">
                    <div className="botany-v2-trust-head">
                      <ThemeIcon
                        size={34}
                        radius="xl"
                        color="orange"
                        variant="light"
                      >
                        <item.icon size={18} />
                      </ThemeIcon>
                      <div>
                        <div className="botany-v2-trust-label">
                          {item.label}
                        </div>
                        <div className="botany-v2-stars">
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <span>{item.score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="botany-v2-form-shell">
              <Paper
                className="botany-v2-form-card"
                radius="xl"
                p={desktop ? "1.5rem" : "1rem"}
              >
                <Stack gap="md">
                  <Title order={3} ta="center" className="botany-v2-form-title">
                    Struggling with your botany assignment?
                  </Title>
                  <div className="botany-v2-form-badges">
                    <span>
                      <IconCheck size={14} /> Better draft clarity
                    </span>
                    <span>
                      <IconCheck size={14} /> No vague feedback
                    </span>
                    <span>
                      <IconCheck size={14} /> Faster turnaround
                    </span>
                  </div>
                  <GetHelpForm
                    formSize="sm"
                    flatForm
                    headerMode="minimal"
                    submitLabel="Get Botany Help"
                  />
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-experts">
          <div className="botany-v2-experts-shell">
            <div className="botany-v2-section-heading botany-v2-section-heading-center">
              <Title order={2}>
                Meet the Scholars: Ph.D. Experts Providing Premier Botany
                Assignment Help
              </Title>
              <Text>
                Our team includes real science-focused mentors who know how to
                explain plant biology simply, clearly, and in a more
                assignment-ready way.
              </Text>
            </div>

            <SimpleGrid cols={{ base: 1, md: 2, xl: 4 }} spacing="lg">
              {scholarCards.map((card) => (
                <Paper
                  key={card.name}
                  className="botany-v2-scholar-card"
                  radius="xl"
                  p="lg"
                >
                  <Stack gap="md" h="100%">
                    <Group align="flex-start" wrap="nowrap">
                      <div className={`botany-v2-avatar ${card.accent}`}>
                        <span>{card.initials}</span>
                        <span className="botany-v2-avatar-status" />
                      </div>
                      <div>
                        <Text fw={900}>{card.name}</Text>
                        <Text size="sm" c="dimmed">
                          {card.degree}
                        </Text>
                        <div className="botany-v2-scholar-stars">
                          {Array.from({ length: 5 }).map((_, index) => (
                            <IconStarFilled
                              key={`${card.name}-${index}`}
                              size={14}
                              className={
                                index < card.stars
                                  ? "botany-v2-star-on"
                                  : "botany-v2-star-off"
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </Group>

                    <Text size="sm" style={{ lineHeight: 1.8 }}>
                      {card.description}
                    </Text>

                    <div className="botany-v2-scholar-footer">
                      <div className="botany-v2-scholar-stats">
                        <span>
                          <IconFileText size={13} />
                          <strong>{card.completedOrders}</strong> Completed
                          Orders
                        </span>
                        <span>
                          <IconMessageCircle size={13} />
                          <strong>{card.studentReviews}</strong> Student Reviews
                        </span>
                      </div>

                      <Button
                        component="a"
                        href={whatsappUrl}
                        radius="md"
                        variant="outline"
                        color="green"
                      >
                        Hire Me
                      </Button>
                    </div>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </div>
        </section>

        <section className="botany-v2-process">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              From Seed to Success: How to Secure Your Professional Botany
              Assignment
            </Title>
          </div>

          <div className="botany-v2-process-flow">
            {processSteps.map((step, index) => (
              <div key={step.title} className="botany-v2-process-node">
                <div className="botany-v2-process-item">
                  <div className="botany-v2-process-icon-shell">
                    <step.icon size={56} stroke={1.5} />
                  </div>
                  <Title order={3}>{step.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {step.description}
                  </Text>
                </div>

                {index < processSteps.length - 1 ? (
                  <div className="botany-v2-process-arrow" aria-hidden="true">
                    <IconArrowRight size={44} stroke={2.2} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="botany-v2-process-cta">
            <Button
              component={Link}
              to={orderPagePath}
              color="green"
              radius="md"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-features">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              Why We Provide the Best Botany Assignment Help in the United
              States
            </Title>
            <Text>
              We make sure your science work is done correctly. We focus on
              quality, speed, and facts.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="xl">
            {featurePillars.map((item) => (
              <div key={item.title} className="botany-v2-feature-row">
                <ThemeIcon
                  size={64}
                  radius="xl"
                  color="green"
                  variant="light"
                  className="botany-v2-feature-icon"
                >
                  <item.icon size={28} />
                </ThemeIcon>
                <Stack gap={6} className="botany-v2-feature-copy">
                  <Title order={3}>{item.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {item.description}
                  </Text>
                </Stack>
              </div>
            ))}
          </SimpleGrid>

          <div className="botany-v2-feature-cta">
            <Button
              component={Link}
              to={orderPagePath}
              radius="md"
              color="green"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-reviews">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              What Students Say About Our Botany Assignment Help
            </Title>
            <Text>
              Reviews from students who wanted clearer structure, faster
              feedback, and stronger final submissions.
            </Text>
          </div>

          <div className="botany-v2-review-platforms">
            {trustSignals.map((item) => (
              <div key={item.label} className="botany-v2-platform-pill">
                <item.icon size={16} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {reviewCards.map((review) => (
              <Paper
                key={review.title}
                className="botany-v2-review-card"
                radius="lg"
                p="lg"
              >
                <Stack gap="sm">
                  <div className="botany-v2-stars">
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                  </div>
                  <Text fw={800}>{review.title}</Text>
                  <Text size="xs" c="dimmed">
                    {review.meta}
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.8 }}>
                    {review.quote}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {review.date}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </section>

        <section className="botany-v2-urgent">
          <Paper
            className="botany-v2-urgent-card"
            radius="xl"
            p={desktop ? "2rem" : "1.25rem"}
          >
            <Grid align="center" gutter="xl">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <div className="botany-v2-urgent-visual">
                  <IconNotebook size={84} />
                  <IconMicroscope
                    size={34}
                    className="botany-v2-float botany-v2-float-one"
                  />
                  <IconLeaf
                    size={30}
                    className="botany-v2-float botany-v2-float-two"
                  />
                  <IconFlask2
                    size={30}
                    className="botany-v2-float botany-v2-float-three"
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Title order={2}>
                    Get Urgent Botany Homework Help for Same-Day Delivery
                  </Title>
                  <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                    Access urgent botany assignment help for reports, plant
                    science write-ups, and draft improvement when the deadline
                    is close and the next steps need to be clearer fast.
                  </Text>
                  <Button
                    component={Link}
                    to={orderPagePath}
                    radius="md"
                    color="green"
                    leftSection={<IconClockHour4 size={18} />}
                    className="botany-v2-urgent-button"
                  >
                    Order Support
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>
        </section>

        <section className="botany-v2-services">
          <div className="botany-v2-services-grid">
            <div className="botany-v2-services-left">
              <Title order={2}>
                Our Botany Writing Services Cover Every Academic Need
              </Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                SubjectBuddy supports botany reports, essays, fieldwork
                write-ups, and technical plant science assignments that need
                clearer structure, stronger explanation, and more confident
                academic flow.
              </Text>
              <Text fw={800}>Type of support</Text>
              <ul className="botany-v2-service-list">
                {serviceTopics.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="botany-v2-services-right">
              <Paper className="botany-v2-freebies-card" radius="xl" p="lg">
                <Stack gap="md">
                  <Text fw={900} tt="uppercase" size="sm" c="orange">
                    Included support areas
                  </Text>
                  {freeSupportItems.map((item) => (
                    <div key={item} className="botany-v2-freebie-row">
                      <span className="botany-v2-freebie-label">
                        <IconCheck size={16} />
                        {item}
                      </span>
                      <span className="botany-v2-freebie-value">Included</span>
                    </div>
                  ))}
                  <Button
                    component="a"
                    href={whatsappUrl}
                    radius="md"
                    color="orange"
                  >
                    Get Support
                  </Button>
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-content">
          <div className="botany-v2-content-scroll">
            <div className="botany-v2-content-grid">
              {contentSections.map((section) => (
                <article key={section.title} className="botany-v2-article-card">
                  <Stack gap="md">
                    <Title order={2}>{section.title}</Title>
                    {section.paragraphs.map((paragraph) => (
                      <Text
                        key={paragraph}
                        c="dimmed"
                        style={{ lineHeight: 1.9 }}
                      >
                        {paragraph}
                      </Text>
                    ))}
                    <ul className="botany-v2-article-list">
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </Stack>
                </article>
              ))}

              <article className="botany-v2-article-card">
                <Stack gap="md">
                  <Title order={2}>{sampleSection.title}</Title>
                  <Paper className="botany-v2-sample-card" radius="lg" p="lg">
                    <Stack gap="sm">
                      <Title order={3}>{sampleSection.heading}</Title>
                      <Text c="dimmed" style={{ lineHeight: 1.85 }}>
                        {sampleSection.copy}
                      </Text>
                    </Stack>
                  </Paper>
                </Stack>
              </article>
            </div>
          </div>
        </section>

        <section className="botany-v2-live-reviews">
          <ReviewsPreviewSection
            title="Recent Student Feedback on Student Support"
            description="Selected student feedback that reflects clearer structure, better scientific explanation, and calmer deadline support."
            reviews={relatedReviews.length ? relatedReviews : usReviews}
            compactCards
            plainTrustBar
          />
        </section>

        <section className="botany-v2-faqs">
          <div className="botany-v2-section-heading">
            <Title order={2}>FAQs on Botany Assignment Help</Title>
          </div>
          <AssignmentFAQ faqs={botanyFaqs} />
        </section>
      </Container>
    </main>
  );
}

export default BotanyAssignmentPage;
